import type {
  MobileDeviceContext,
  MobileUserExtraContext,
  UserExtraContext,
  UserExtraPermissionsContext,
  WebUserExtraContext,
} from '../../auth/nocode/types';
import type { DeviceContextInStore } from '../stores/app/GlobalAppStateStore/types';

// Both directions of the device↔deviceInfo / geography↔location rename live in this file
// on purpose — they must stay exact inverses; the destructures below are the runtime
// implementation. The `satisfies` clause derives the key sets from the type
// definitions themselves, so the table cannot drift from the shapes it renames. The
// builder device schemas (apps/platform useGetDeviceSchema.ts) declare the same
// `deviceInfo`/`location` keys directly — keep them in sync with this table.
export const DEVICE_CONTEXT_KEY_RENAMES = {
  device: 'deviceInfo',
  geography: 'location',
} as const satisfies Record<
  Exclude<keyof MobileUserExtraContext, keyof MobileDeviceContext>,
  Exclude<keyof MobileDeviceContext, keyof MobileUserExtraContext>
>;

// legacy mirror of the store slice — anchors the reverse rename so a typo'd or
// forgotten key is a compile error instead of a silently broken legacy view
type LegacyDeviceContextView = Partial<Omit<MobileUserExtraContext, 'device' | 'permissions'>> & {
  browser?: WebUserExtraContext['browser'];
  device?: DeviceContextInStore['deviceInfo'];
  permissions?: Partial<UserExtraPermissionsContext>;
};

/**
 * Legacy runtime-env (server exchange / UserStore shape, inner `device`/`geography`
 * keys) → canonical `deviceContext` store slice (`deviceInfo`/`location`).
 */
export function adaptUserExtraContextToDeviceContext(
  extraContext: UserExtraContext | undefined,
): DeviceContextInStore | undefined {
  if (!extraContext) {
    return undefined;
  }

  const { device: deviceInfo, geography, ...rest } = extraContext;
  const adapted: DeviceContextInStore = { ...rest };
  if (deviceInfo !== undefined) {
    adapted.deviceInfo = deviceInfo;
  }
  if (geography !== undefined) {
    adapted.location = geography;
  }
  return adapted;
}

function getLegacyExtraContext(deviceContext: DeviceContextInStore): UserExtraContext {
  const { deviceInfo, location, ...rest } = deviceContext;
  const legacy: LegacyDeviceContextView = { ...rest };
  if (deviceInfo !== undefined) {
    legacy.device = deviceInfo;
  }
  if (location !== undefined) {
    legacy.geography = location;
  }
  // partial→full compat cast: the legacy contract always claimed a complete env even
  // while it was populated incrementally — confined to this one expression
  return legacy as UserExtraContext;
}

/**
 * Compute-context `userContext` entry: the store's user fields plus a derived legacy
 * `context` view of the `deviceContext` slice, so pre-existing
 * `userContext.context.*` bindings keep resolving after the runtime env moved to the
 * first-class device object.
 *
 * Plain adaptation — derived objects get a fresh identity per call; the
 * ContextBuilder base-context cache bounds how often the compute-context view is
 * rebuilt. Generic because callers hold different user shapes (store
 * `UserContextInStore`, generated `User`, loose records).
 */
export function getUserContextWithLegacyDeviceContext<TUserContext extends object>(
  userContext: TUserContext | undefined,
  deviceContext: DeviceContextInStore | undefined,
): (TUserContext & { context?: UserExtraContext }) | undefined {
  if (!deviceContext || Object.keys(deviceContext).length === 0) {
    return userContext;
  }

  return {
    ...userContext,
    context: getLegacyExtraContext(deviceContext),
  } as TUserContext & { context?: UserExtraContext };
}
